(() => {
  if (!customElements.get('origin-announcement')) {
    customElements.define('origin-announcement', class extends HTMLElement {
      connectedCallback() {
        this.messages = [...this.querySelectorAll('[data-announcement-message]')];
        if (this.messages.length < 2) return;
        this.index = Math.max(0, this.messages.findIndex((message) => !message.hidden));
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        ['pointerenter', 'pointerleave', 'focusin', 'focusout'].forEach((eventName) => {
          this.addEventListener(eventName, () => this.updateRotation(), options);
        });
        document.addEventListener('visibilitychange', () => this.updateRotation(), options);
        window.addEventListener('pagehide', () => clearInterval(this.timer), options);
        window.addEventListener('pageshow', () => this.updateRotation(), options);
        this.motionPreference.addEventListener('change', () => this.updateRotation(), options);
        this.updateRotation();
      }

      updateRotation() {
        clearInterval(this.timer);
        if (this.motionPreference.matches || document.hidden || this.matches(':hover, :focus-within')) return;
        this.timer = setInterval(() => {
          this.messages[this.index].hidden = true;
          this.index = (this.index + 1) % this.messages.length;
          const message = this.messages[this.index];
          message.hidden = false;
          if (!this.motionPreference.matches) {
            message.animate([{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 280, easing: 'ease-out' });
          }
        }, 4000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        this.events?.abort();
      }
    });
  }

  const header = document.querySelector('.origin-header:not(.origin-header--studio)');
  if (header) {
    let scrollFrame = 0;
    function updateHeader() {
      const compact = header.classList.contains('is-compact');
      header.classList.toggle('is-compact', window.scrollY > (compact ? 32 : 80));
      scrollFrame = 0;
    }
    window.addEventListener('scroll', () => {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(updateHeader);
    }, { passive: true });
    window.addEventListener('pageshow', updateHeader);
    updateHeader();
  }

  const aboutMenus = document.querySelectorAll('.origin-about-menu');
  function closeOutsideAboutMenus(event) {
    aboutMenus.forEach((menu) => {
      if (menu.open && !menu.contains(event.target)) menu.open = false;
    });
  }
  document.addEventListener('pointerdown', closeOutsideAboutMenus);
  document.addEventListener('focusin', closeOutsideAboutMenus);
  aboutMenus.forEach((menu) => {
    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !menu.open) return;
      event.preventDefault();
      event.stopPropagation();
      menu.open = false;
      menu.querySelector('summary').focus();
    });
  });

  if (typeof HTMLDialogElement === 'undefined') return;
  const triggers = document.querySelectorAll('[data-origin-open]');
  const dialogs = document.querySelectorAll('[data-origin-dialog]');
  const returnTargets = new WeakMap();
  const closingAnimations = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function closeDialog(dialog) {
    if (!dialog.open || closingAnimations.has(dialog)) return;
    if (reducedMotion.matches || typeof dialog.animate !== 'function') {
      dialog.close();
      return;
    }
    const style = getComputedStyle(dialog);
    const duration = parseFloat(style.getPropertyValue('--origin-panel-close-duration')) || 160;
    const animation = dialog.animate([
      { opacity: style.opacity, transform: style.transform },
      {
        opacity: style.getPropertyValue('--origin-panel-closed-opacity').trim() || '0',
        transform: style.getPropertyValue('--origin-panel-offset').trim() || 'translateY(12px)',
      },
    ], { duration, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' });
    dialog.classList.add('is-closing');
    closingAnimations.set(dialog, animation);
    const finish = () => {
      if (closingAnimations.get(dialog) !== animation) return;
      closingAnimations.delete(dialog);
      dialog.close();
      animation.cancel();
    };
    animation.finished.then(finish, finish);
  }

  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    dialogs.forEach((dialog) => closingAnimations.get(dialog)?.finish());
  });

  triggers.forEach((trigger) => {
    const dialog = document.getElementById(trigger.dataset.originOpen);
    if (!dialog) return;
    trigger.hidden = false;
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', dialog.id);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (dialog.open) return;
      returnTargets.set(dialog, trigger);
      dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('origin-dialog-open');
    });
  });

  dialogs.forEach((dialog) => {
    dialog.addEventListener('close', () => {
      const animation = closingAnimations.get(dialog);
      closingAnimations.delete(dialog);
      animation?.cancel();
      dialog.classList.remove('is-closing');
      if (!document.querySelector('[data-origin-dialog][open]')) {
        document.documentElement.classList.remove('origin-dialog-open');
      }
      const trigger = returnTargets.get(dialog);
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener('submit', (event) => {
      if (!event.target.matches('form[method="dialog"]')) return;
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target.closest('.origin-about-menu__links a')) {
        closeDialog(dialog);
        return;
      }
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      if (outside) closeDialog(dialog);
    });
  });
})();
